/**
 * Shared Advanced Text System Types
 * 
 * These types are extracted from ClassicCaptionPage.tsx and provide
 * consistent configuration across timeloom's caption and text-overlay systems.
 */

/**
 * Text outline effects - matches ClassicCaptionPage functionality
 */
export type OutlineType = 'none' | 'glow' | 'shadow' | '3d-shadow' | 'stroke' | '3d-stroke';

/**
 * Background styling options for text containers
 */
export type BackgroundType = 'none' | 'white' | 'colored' | 'translucent';

/**
 * Advanced text animation types - full ClassicCaptionPage feature set
 */
export type TextAnimationType = 
    | 'none' 
    | 'highlight' 
    | 'boxed' 
    | 'scale' 
    | 'pop' 
    | 'karaoke' 
    | 'reveal' 
    | 'reveal_up' 
    | 'reveal_scale' 
    | 'reveal_box' 
    | 'karaoke_scale' 
    | 'karaoke_box' 
    | 'slide_left' 
    | 'slide_up';

/**
 * Text case transformation options
 */
export type CaptionCase = 'standard' | 'uppercase';

/**
 * Layer types for outline rendering (internal use)
 */
export type LayerType = 'glow' | 'main' | 'other';

/**
 * Shared text styling configuration
 * Used by both TextOverlay and Caption systems for consistent styling
 * Combines all ClassicCaptionPage styling options
 */
export interface TextStyle {
    // Core color configuration
    primaryColor: string;
    currentSpokenWordColor: string;
    
    // Font and sizing
    fontFamily: string;
    maxFontSize: number;
    
    // Visual effects
    outline: OutlineType;
    background: BackgroundType;
    textAnimation: TextAnimationType;
    borderRadius: number;
    
    // Text processing
    captionCase: CaptionCase;
    hidePunctuation: boolean;
    
    // Layout padding (relative to font size)
    containerPadding: {
        x: number; // Horizontal padding multiplier
        y: number; // Vertical padding multiplier
    };
    boxPadding: {
        x: number; // Box padding multiplier for boxed animations
        y: number; // Box padding multiplier for boxed animations
    };
}

/**
 * @deprecated Use TextStyle instead
 * Comprehensive text styling configuration
 * Combines all ClassicCaptionPage styling options
 */
export interface AdvancedTextConfig extends TextStyle {}

/**
 * Text token for word-by-word animation timing
 * Compatible with both caption and text-overlay systems
 */
export interface TextToken {
    text: string;
    fromMs: number;
    toMs: number;
    index?: number;
    verticalOffset?: number;
    style?: {
        fontSize?: number;
        fontWeight?: string;
        color?: string;
        backgroundColor?: string;
        borderRadius?: number;
    };
}

/**
 * Text page data structure for rendering
 * Abstracts the content from both caption and text-overlay sources
 */
export interface TextPageData {
    text: string;
    tokens?: TextToken[];
    startMs: number;
}

/**
 * Advanced text engine props
 * Core interface for the shared rendering engine
 * Accepts either the new TextStyle or legacy AdvancedTextConfig
 */
export interface AdvancedTextEngineProps {
    page: TextPageData;
    config: TextStyle | AdvancedTextConfig; // Accept both interfaces
    videoConfig: {
        fps: number;
        width: number;
        height: number;
    };
    currentFrame: number;
    position?: 'center' | 'top' | 'bottom'; // Position hint for canvas sizing
    // Available dimensions for responsive font sizing
    availableWidth?: number;
    availableHeight?: number;
}

/**
 * Render result from outline calculation (internal use)
 */
export interface OutlineRenderProps {
    outline: OutlineType;
    layerType: LayerType;
    token: TextToken;
    index: number;
    positions: Array<{ x: number; width: number }>;
    verticalPosition: number;
    fontSize: number;
    fontFamily: string;
    outlineColor?: string;
    opacity: number;
    combinedTransform: string;
    processText: (text: string) => string;
    textAnchor?: 'start' | 'middle' | 'end';
}

/**
 * Default text styling configuration
 * Provides sensible defaults matching ClassicCaptionPage behavior
 * Used by both TextOverlay and Caption systems
 */
export const DEFAULT_TEXT_STYLE: TextStyle = {
    primaryColor: '#ffffff',
    currentSpokenWordColor: '#ffff00',
    fontFamily: 'Arial',
    maxFontSize: 60,
    outline: 'none',
    background: 'none',
    textAnimation: 'none',
    borderRadius: 0.1,
    captionCase: 'standard',
    hidePunctuation: false,
    containerPadding: {
        x: 0.1,
        y: 0.05
    },
    boxPadding: {
        x: 0,
        y: 0
    }
};

/**
 * @deprecated Use DEFAULT_TEXT_STYLE instead
 */
export const DEFAULT_ADVANCED_TEXT_CONFIG: AdvancedTextConfig = DEFAULT_TEXT_STYLE;

/**
 * Utility type for basic text styling (backward compatibility)
 */
export interface BasicTextStyle {
    fontSize?: number;
    fontFamily?: string;
    color?: string;
    textAlign?: 'left' | 'center' | 'right';
    backgroundColor?: string;
    padding?: number;
    borderRadius?: number;
}

/**
 * Text rendering mode selection
 */
export type TextRenderingMode = 'basic' | 'advanced';

/**
 * Combined text configuration that supports both rendering modes
 */
export interface FlexibleTextConfig {
    // Basic configuration (always available)
    basicStyle?: BasicTextStyle;
    
    // Advanced configuration (optional - enables advanced features)
    advancedConfig?: AdvancedTextConfig;
    
    // Token data (optional - enables word-by-word animations)
    tokens?: TextToken[];
}

/**
 * Font map type (to integrate with clipCreator font system)
 */
export interface FontMap {
    [key: string]: string;
}